import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PlusCircle, Trash2, Clock, CheckCircle2, XCircle, Eye, ThumbsUp } from "lucide-react";

const STATUS_BADGE = {
  pending: { label: "En attente", cls: "bg-amber-100 text-amber-800", Icon: Clock },
  published: { label: "Publié", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  rejected: { label: "Refusé", cls: "bg-red-100 text-red-800", Icon: XCircle },
};

export default function MyTutorials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = () => {
    setLoading(true);
    api
      .get("/tutorials/mine/list")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce tuto ? Cette action est définitive.")) return;
    try {
      await api.delete(`/tutorials/${id}`);
      toast.success("Tuto supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur suppression");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-12 lg:py-16" data-testid="my-tutorials-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="eyebrow text-secondary">Mon espace</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">
            Mes tutos
          </h1>
        </div>
        <Link to="/tutos/nouveau">
          <Button className="rounded-full h-11 px-6" data-testid="btn-new-tuto">
            <PlusCircle className="h-4 w-4 mr-2" /> Nouveau tuto
          </Button>
        </Link>
      </div>

      {loading ? (
        <p className="mt-12 text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <Card className="mt-10 p-10 border-dashed text-center" data-testid="my-tuto-empty">
          <p className="text-sm text-muted-foreground">
            Vous n'avez pas encore publié de tuto.
          </p>
          <Link to="/tutos/nouveau">
            <Button className="mt-4 rounded-full">Créer mon premier tuto</Button>
          </Link>
        </Card>
      ) : (
        <div className="mt-10 space-y-4">
          {items.map((t) => {
            const s = STATUS_BADGE[t.status] || STATUS_BADGE.pending;
            const Icon = s.Icon;
            return (
              <Card key={t.id} className="p-5" data-testid={`my-tuto-item-${t.slug}`}>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-full sm:w-32 shrink-0 aspect-video rounded-lg bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 overflow-hidden">
                    {t.cover_photo_url ? (
                      <img src={`${backend}${t.cover_photo_url}`} alt={t.title} className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge className={`rounded-full ${s.cls} border-0 gap-1`}>
                        <Icon className="h-3 w-3" /> {s.label}
                      </Badge>
                      <Badge variant="secondary" className="rounded-full capitalize">{t.category.replace("-", " ")}</Badge>
                      {t.spa_brand && <Badge variant="outline" className="rounded-full">{t.spa_brand}</Badge>}
                    </div>
                    <h3 className="mt-2 font-display font-bold text-lg leading-tight">
                      {t.status === "published" ? (
                        <Link to={`/tutos/${t.slug}`} className="hover:text-primary" data-testid={`my-tuto-link-${t.slug}`}>
                          {t.title}
                        </Link>
                      ) : (
                        t.title
                      )}
                    </h3>
                    {t.summary && <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{t.summary}</p>}
                    {t.status === "rejected" && t.reject_reason && (
                      <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                        <strong>Motif du refus :</strong> {t.reject_reason}
                      </p>
                    )}
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-3 text-muted-foreground">
                        {t.status === "published" && (
                          <>
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {t.views}</span>
                            <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> {t.helpful_count}</span>
                          </>
                        )}
                        <span>{new Date(t.created_at).toLocaleDateString("fr-FR")}</span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(t.id)}
                        className="text-red-600 hover:text-red-800 h-8"
                        data-testid={`my-tuto-delete-${t.slug}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
